import {
  AccordionKeys,
  OptimizationTypes,
} from '@/enums/portfolio-optimization.enum'
import {
  IConstraint,
  IOptimizationParameters,
  IOptimizationPayload,
  IPortfolio,
  IScreeningCriterias,
} from '@/interfaces/portfolioOptimization.interface'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface IPortfolioOptimizationGlobalState {
  isComplete: boolean
  accordions: Record<AccordionKeys, boolean>
  selectedPortfolios: IPortfolio[]
  showCashflowReport: boolean
  formData: IOptimizationPayload
}

export const initialState: IPortfolioOptimizationGlobalState = {
  isComplete: false,
  accordions: {
    [AccordionKeys.SELECT_PORTFOLIOS]: false,
    [AccordionKeys.OPTIMIZATION_PARAMETER]: false,
    [AccordionKeys.CANDIDATE_SCREEN]: false,
    [AccordionKeys.OPTIMIZATION_CONSTRAINTS]: false,
  },
  selectedPortfolios: [],
  showCashflowReport: true,
  formData: {
    id: undefined,
    name: undefined,
    optimizationParams: {
      params: {
        optimizationType: OptimizationTypes.Horizon,
        objectiveType: '',
        objectiveItem: '',
        liabilityReinvestmentTaxes: {
          specifyLiabilityToMatch: false,
          specifyReinvestmentRates: false,
          specifyBorrowingRates: false,
          useCashFlows: false,
          liabilityToMatch: [],
          reinvestmentRates: [],
          borrowingRates: [],
          cashFlows: {
            option: null,
            date: null,
            state: null,
            gc_inc: null,
            gc_cap: null,
            grand_inc: null,
            grand_cap: null,
            nongrand_inc: null,
            nongrand_cap: null,
            zero_inc: null,
          },
        },
        contributionWithdrawal: '',
        amount: null,
      },
    },
    screenings: [],
    constraints: [],
  },
}

export const portfolioOptimizationSlice = createSlice({
  name: 'portfolioOptimization',
  initialState,
  reducers: {
    setAccordionComplete: (
      state,
      action: PayloadAction<{
        accordionType: AccordionKeys
        isComplete: boolean
      }>,
    ) => {
      const key = action.payload.accordionType
      state.accordions[key] = action.payload.isComplete
    },
    checkAllAccordionsComplete: (state) => {
      if (
        state.accordions[AccordionKeys.SELECT_PORTFOLIOS] &&
        state.accordions[AccordionKeys.OPTIMIZATION_PARAMETER] &&
        state.accordions[AccordionKeys.CANDIDATE_SCREEN] &&
        state.accordions[AccordionKeys.OPTIMIZATION_CONSTRAINTS]
      ) {
        state.isComplete = true
      } else {
        state.isComplete = false
      }
    },
    setSelectedPortfolios: (state, action: PayloadAction<IPortfolio[]>) => {
      state.selectedPortfolios = action.payload
    },
    clearSelectedPortfolios: (state) => {
      state.selectedPortfolios = []
    },
    setComplete: (state, action: PayloadAction<boolean>) => {
      state.isComplete = action.payload
    },
    clearAll: (state) => {
      state.accordions = initialState.accordions
      state.isComplete = initialState.isComplete
      state.selectedPortfolios = initialState.selectedPortfolios
      state.showCashflowReport = initialState.showCashflowReport
      state.formData = initialState.formData
    },
    showCashflowReport: (state, action: PayloadAction<boolean>) => {
      state.showCashflowReport = action.payload
    },
    setOptimizationParams: (
      state,
      action: PayloadAction<IOptimizationParameters>,
    ) => {
      state.formData.optimizationParams = action.payload
    },
    clearOptimizationParams: (state) => {
      state.formData.optimizationParams =
        initialState.formData.optimizationParams
    },
    setScreeningItems: (
      state,
      action: PayloadAction<IScreeningCriterias[]>,
    ) => {
      state.formData.screenings = action.payload
    },
    clearScreeningItems: (state) => {
      state.formData.screenings = initialState.formData.screenings
    },
    setConstraintItems: (state, action: PayloadAction<IConstraint[]>) => {
      state.formData.constraints = action.payload
    },
    clearConstraintItems: (state) => {
      state.formData.constraints = initialState.formData.constraints
    },
    updatePortfolioOptimizationFormData: (
      state,
      action: PayloadAction<IOptimizationPayload>,
    ) => {
      state.formData = action.payload
    },
  },
})

export const {
  setAccordionComplete,
  checkAllAccordionsComplete,
  setSelectedPortfolios,
  clearSelectedPortfolios,
  setComplete,
  clearAll,
  showCashflowReport,
  setOptimizationParams,
  clearOptimizationParams,
  setScreeningItems,
  clearScreeningItems,
  setConstraintItems,
  clearConstraintItems,
  updatePortfolioOptimizationFormData,
} = portfolioOptimizationSlice.actions

export default portfolioOptimizationSlice.reducer
